import { useCallback, useEffect, useState } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import { api } from '../services/api';

interface Ticket {
  id: string;
  ticket_number: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  source: string;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  dealer_contact_id: string | null;
  sales_rep_id: string | null;
  order_id: string | null;
  dealer_first_name: string | null;
  dealer_last_name: string | null;
  dealer_email: string | null;
  dealer_id: string | null;
  company_name: string | null;
  rep_firm_id: string | null;
  rep_firm_name: string | null;
  rep_first_name: string | null;
  rep_last_name: string | null;
  rep_email: string | null;
  order_number: string | null;
  message_count: string | number;
  last_activity_at: string;
  age_days: number | string;
  days_since_activity: number | string;
}

interface SalesRep {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  status: string;
}

interface Stats {
  summary: {
    open_count: string;
    in_progress_count: string;
    unassigned_count: string;
    stale_count: string;
    urgent_count: string;
    total_count: string;
  };
  byRepFirm: Array<{ rep_firm_id: string | null; rep_firm_name: string; open_count: string; unassigned_count: string }>;
  bySalesRep: Array<{ sales_rep_id: string; rep_name: string | null; rep_email: string | null; open_count: string }>;
  staleDays: number;
}

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'bg-red-100 text-red-800 border-red-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  medium: 'bg-blue-100 text-blue-800 border-blue-300',
  low: 'bg-gray-100 text-gray-700 border-gray-300',
};

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-green-100 text-green-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  resolved: 'bg-gray-100 text-gray-700',
  closed: 'bg-gray-100 text-gray-500',
};

function StatTile({
  label,
  value,
  tone,
  active,
  onClick,
}: {
  label: string;
  value: string | number;
  tone: 'default' | 'amber' | 'red' | 'blue';
  active?: boolean;
  onClick?: () => void;
}) {
  const tones: Record<string, string> = {
    default: 'border-l-gray-300',
    amber: 'border-l-amber-500',
    red: 'border-l-red-500',
    blue: 'border-l-blue-500',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left w-full p-4 bg-white rounded-lg shadow-sm border border-gray-200 border-l-4 ${tones[tone]} hover:shadow-md transition-shadow ${
        active ? 'ring-2 ring-blue-500' : ''
      }`}
    >
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-3xl font-bold text-gray-900">{value}</div>
    </button>
  );
}

export default function SupportTicketsPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [view, setView] = useState<'all' | 'unassigned' | 'stale'>('all');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [repFirmFilter, setRepFirmFilter] = useState<string>('');
  const [salesRepFilter, setSalesRepFilter] = useState<string>('');

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reply, setReply] = useState('');

  const loadStats = useCallback(async () => {
    try {
      const s = await api.support.stats(7);
      setStats(s);
    } catch (e: any) {
      setError(e.message || 'Failed to load stats');
    }
  }, []);

  const loadTickets = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, any> = { limit: 100 };
      if (view === 'unassigned') params.unassigned = 'true';
      if (view === 'stale') params.stale_days = 7;
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (repFirmFilter) params.rep_firm_id = repFirmFilter;
      if (salesRepFilter) params.sales_rep_id = salesRepFilter;
      const data = await api.support.list(params);
      setTickets(data.tickets || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [view, statusFilter, priorityFilter, repFirmFilter, salesRepFilter]);

  useEffect(() => {
    loadStats();
    api.support.salesReps().then(setSalesReps).catch(() => {});
  }, [loadStats]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const openDetail = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const d = await api.support.getById(id);
      setDetail(d);
    } catch (e: any) {
      setError(e.message || 'Failed to load ticket detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const updateField = async (id: string, patch: any) => {
    try {
      await api.support.update(id, patch);
      await Promise.all([loadTickets(), loadStats()]);
      if (expandedId === id) {
        const d = await api.support.getById(id);
        setDetail(d);
      }
    } catch (e: any) {
      alert(e.message || 'Update failed');
    }
  };

  const sendReply = async (id: string) => {
    if (!reply.trim()) return;
    try {
      await api.support.addMessage(id, reply.trim());
      setReply('');
      const d = await api.support.getById(id);
      setDetail(d);
      await loadTickets();
    } catch (e: any) {
      alert(e.message || 'Failed to send reply');
    }
  };

  const fmtDate = (s: string) => new Date(s).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  const fmtDays = (n: number | string) => {
    const v = typeof n === 'string' ? parseFloat(n) : n;
    if (!Number.isFinite(v)) return '—';
    if (v < 1) return `${Math.round(v * 24)}h`;
    return `${Math.round(v)}d`;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Support Tickets"
        description="Factory oversight of all dealer support tickets — make sure assigned reps and rep firms are responding."
      />

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">{error}</div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatTile
            label="Open"
            value={stats.summary.open_count}
            tone="blue"
            active={view === 'all'}
            onClick={() => setView('all')}
          />
          <StatTile label="In Progress" value={stats.summary.in_progress_count} tone="default" />
          <StatTile
            label="Unassigned"
            value={stats.summary.unassigned_count}
            tone="amber"
            active={view === 'unassigned'}
            onClick={() => setView('unassigned')}
          />
          <StatTile
            label={`Stale > ${stats.staleDays}d`}
            value={stats.summary.stale_count}
            tone="red"
            active={view === 'stale'}
            onClick={() => setView('stale')}
          />
          <StatTile label="Urgent / High" value={stats.summary.urgent_count} tone="red" />
        </div>
      )}

      {stats && stats.byRepFirm.length > 0 && (
        <Card title="Open tickets by rep firm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500">
                  <th className="px-3 py-2">Rep firm</th>
                  <th className="px-3 py-2 text-right">Open</th>
                  <th className="px-3 py-2 text-right">Unassigned</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {stats.byRepFirm.map((row, idx) => (
                  <tr key={idx} className="border-t border-gray-100">
                    <td className="px-3 py-2">{row.rep_firm_name}</td>
                    <td className="px-3 py-2 text-right font-mono">{row.open_count}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {parseInt(row.unassigned_count) > 0 ? (
                        <span className="text-amber-700 font-semibold">{row.unassigned_count}</span>
                      ) : (
                        row.unassigned_count
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {row.rep_firm_id && (
                        <button
                          onClick={() => {
                            setRepFirmFilter(row.rep_firm_id!);
                            setView('all');
                          }}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Filter →
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card>
        <div className="flex flex-wrap gap-3 items-center mb-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded text-sm"
          >
            <option value="">All statuses (excl. resolved/closed)</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded text-sm"
          >
            <option value="">Any priority</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select
            value={salesRepFilter}
            onChange={(e) => setSalesRepFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded text-sm"
          >
            <option value="">Any sales rep</option>
            {salesReps.map((r) => (
              <option key={r.id} value={r.id}>
                {r.first_name} {r.last_name}
              </option>
            ))}
          </select>
          {(repFirmFilter || salesRepFilter || statusFilter || priorityFilter || view !== 'all') && (
            <button
              onClick={() => {
                setStatusFilter('');
                setPriorityFilter('');
                setRepFirmFilter('');
                setSalesRepFilter('');
                setView('all');
              }}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Reset filters
            </button>
          )}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : tickets.length === 0 ? (
          <div className="text-center py-10 text-gray-500">No tickets match.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                  <th className="px-3 py-2">Ticket</th>
                  <th className="px-3 py-2">Dealer</th>
                  <th className="px-3 py-2">Rep firm</th>
                  <th className="px-3 py-2">Assigned to</th>
                  <th className="px-3 py-2">Priority</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Age</th>
                  <th className="px-3 py-2 text-right">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => {
                  const isExpanded = expandedId === t.id;
                  const isStale = (typeof t.days_since_activity === 'string' ? parseFloat(t.days_since_activity) : t.days_since_activity) >= 7;
                  return (
                    <>
                      <tr
                        key={t.id}
                        onClick={() => openDetail(t.id)}
                        className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                          isExpanded ? 'bg-blue-50' : ''
                        }`}
                      >
                        <td className="px-3 py-2">
                          <div className="font-mono text-xs text-gray-500">{t.ticket_number}</div>
                          <div className="font-medium text-gray-900">{t.subject}</div>
                          <div className="text-xs text-gray-500">{t.message_count} message{Number(t.message_count) === 1 ? '' : 's'}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-gray-900">{t.company_name || '—'}</div>
                          <div className="text-xs text-gray-500">
                            {[t.dealer_first_name, t.dealer_last_name].filter(Boolean).join(' ') || t.dealer_email || '—'}
                          </div>
                        </td>
                        <td className="px-3 py-2">{t.rep_firm_name || <span className="text-gray-400">Direct</span>}</td>
                        <td className="px-3 py-2">
                          {t.sales_rep_id ? (
                            <div>
                              <div className="text-gray-900">
                                {[t.rep_first_name, t.rep_last_name].filter(Boolean).join(' ')}
                              </div>
                              <div className="text-xs text-gray-500">{t.rep_email}</div>
                            </div>
                          ) : (
                            <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800 border border-amber-300">
                              Unassigned
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 text-xs rounded border ${PRIORITY_COLORS[t.priority] || ''}`}>
                            {t.priority}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 text-xs rounded ${STATUS_COLORS[t.status] || ''}`}>
                            {t.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right text-gray-600 font-mono">{fmtDays(t.age_days)}</td>
                        <td className={`px-3 py-2 text-right font-mono ${isStale ? 'text-red-700 font-semibold' : 'text-gray-600'}`}>
                          {fmtDays(t.days_since_activity)} ago
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-blue-50">
                          <td colSpan={8} className="px-4 py-4">
                            {detailLoading || !detail ? (
                              <LoadingSpinner />
                            ) : (
                              <div className="grid md:grid-cols-3 gap-4">
                                <div className="md:col-span-2 space-y-3">
                                  <div className="bg-white rounded p-3 border border-gray-200">
                                    <div className="text-xs uppercase text-gray-500 mb-2">Conversation</div>
                                    <div className="space-y-3 max-h-96 overflow-y-auto">
                                      {(detail.messages || []).map((m: any) => (
                                        <div key={m.id} className="border-l-2 border-blue-200 pl-3">
                                          <div className="text-xs text-gray-500 mb-1">
                                            <span className="font-semibold text-gray-700">{m.sender_name || m.sender_type}</span>
                                            {' · '}
                                            <span>{fmtDate(m.created_at)}</span>
                                          </div>
                                          <div className="text-sm text-gray-900 whitespace-pre-wrap">{m.message}</div>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="mt-3 flex gap-2">
                                      <textarea
                                        value={reply}
                                        onChange={(e) => setReply(e.target.value)}
                                        placeholder="Reply on behalf of factory…"
                                        rows={2}
                                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                                      />
                                      <button
                                        onClick={() => sendReply(t.id)}
                                        disabled={!reply.trim()}
                                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded disabled:opacity-40"
                                      >
                                        Send
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <div className="space-y-3">
                                  <div className="bg-white rounded p-3 border border-gray-200">
                                    <label className="block text-xs uppercase text-gray-500 mb-1">Status</label>
                                    <select
                                      value={t.status}
                                      onChange={(e) => updateField(t.id, { status: e.target.value })}
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                    >
                                      <option value="open">Open</option>
                                      <option value="in_progress">In Progress</option>
                                      <option value="resolved">Resolved</option>
                                      <option value="closed">Closed</option>
                                    </select>
                                  </div>
                                  <div className="bg-white rounded p-3 border border-gray-200">
                                    <label className="block text-xs uppercase text-gray-500 mb-1">Priority</label>
                                    <select
                                      value={t.priority}
                                      onChange={(e) => updateField(t.id, { priority: e.target.value })}
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                    >
                                      <option value="low">Low</option>
                                      <option value="medium">Medium</option>
                                      <option value="high">High</option>
                                      <option value="urgent">Urgent</option>
                                    </select>
                                  </div>
                                  <div className="bg-white rounded p-3 border border-gray-200">
                                    <label className="block text-xs uppercase text-gray-500 mb-1">Assigned sales rep</label>
                                    <select
                                      value={t.sales_rep_id || ''}
                                      onChange={(e) =>
                                        updateField(t.id, { sales_rep_id: e.target.value || null })
                                      }
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                    >
                                      <option value="">— Unassigned —</option>
                                      {salesReps.map((r) => (
                                        <option key={r.id} value={r.id}>
                                          {r.first_name} {r.last_name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  {t.order_number && (
                                    <div className="bg-white rounded p-3 border border-gray-200 text-sm">
                                      <div className="text-xs uppercase text-gray-500 mb-1">Order</div>
                                      <a
                                        href={`/orders/${t.order_id}`}
                                        className="text-blue-600 hover:underline font-mono"
                                      >
                                        {t.order_number}
                                      </a>
                                    </div>
                                  )}
                                  <div className="bg-white rounded p-3 border border-gray-200 text-xs text-gray-600">
                                    <div>Source: {t.source}</div>
                                    <div>Created: {fmtDate(t.created_at)}</div>
                                    {t.resolved_at && <div>Resolved: {fmtDate(t.resolved_at)}</div>}
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
